import { Reveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/SectionLabel";
import { SECRETARIAT_TIERS } from "@/data/eventData";

const MemberCard = ({ member }) => (
    <div className="group" data-testid={`secretariat-member-${member.name.toLowerCase().replace(/\s+/g, "-")}`}>
        <div className="relative aspect-[3/4] overflow-hidden border border-cream/10 bg-coal">
            <img
                src={member.photo}
                alt={member.name}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
        </div>
        <p className="mt-4 font-serif text-xl font-light tracking-tight text-cream">
            {member.name}
        </p>
        <p className="mt-1.5 font-sans text-[10px] font-semibold uppercase tracking-[0.25em] text-scarlet">
            {member.designation}
        </p>
    </div>
);

const PlaceholderCard = ({ tierTitle, id }) => (
    <div data-testid={`secretariat-placeholder-${id}`}>
        <div className="relative flex aspect-[3/4] flex-col items-center justify-center gap-5 overflow-hidden border border-cream/10 bg-coal/50">
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_100%,rgba(201,31,38,0.1),transparent_65%)]"
            />
            <span aria-hidden="true" className="font-serif text-6xl font-light italic leading-none text-cream/10">
                —
            </span>
            <span className="relative font-sans text-[9px] font-semibold uppercase tracking-[0.3em] text-ash/40">
                Portrait to be added
            </span>
        </div>
        <p className="mt-4 font-serif text-xl font-light tracking-tight text-cream/50">
            To be announced
        </p>
        <p className="mt-1.5 font-sans text-[10px] font-semibold uppercase tracking-[0.25em] text-scarlet/70">
            {tierTitle}
        </p>
    </div>
);

const Secretariat = () => (
    <section
        id="secretariat"
        data-testid="secretariat-section"
        className="scroll-mt-24 bg-coal/50 py-24 md:py-36"
    >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <Reveal>
                <SectionLabel index="05" text="The Secretariat" />
            </Reveal>
            <div className="mt-8 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                <Reveal delay={0.1}>
                    <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
                        The People
                        <br />
                        Behind the <em className="italic text-scarlet">Gavel.</em>
                    </h2>
                </Reveal>
                <Reveal delay={0.2}>
                    <p className="max-w-sm text-sm font-light leading-relaxed text-ash md:text-base">
                        The Secretariat steering every committee, session and ceremony
                        of I-ACE Chapter 4.
                    </p>
                </Reveal>
            </div>

            {SECRETARIAT_TIERS.map((tier) => (
                <div
                    key={tier.id}
                    data-testid={`secretariat-tier-${tier.id}`}
                    className="mt-14 md:mt-16"
                >
                    <Reveal>
                        <div className="flex items-center gap-4">
                            <span className="h-px w-10 bg-crimson/60" aria-hidden="true" />
                            <h3 className="font-sans text-[11px] font-semibold uppercase tracking-[0.35em] text-ash">
                                {tier.title}
                            </h3>
                        </div>
                    </Reveal>
                    <div className="mt-8 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
                        {tier.members.length > 0
                            ? tier.members.map((member, i) => (
                                  <Reveal key={member.name} delay={i * 0.06}>
                                      <MemberCard member={member} />
                                  </Reveal>
                              ))
                            : Array.from({ length: tier.placeholders }).map((_, i) => (
                                  <Reveal key={i} delay={i * 0.06}>
                                      <PlaceholderCard
                                          tierTitle={tier.title}
                                          id={`${tier.id}-${i}`}
                                      />
                                  </Reveal>
                              ))}
                    </div>
                </div>
            ))}
        </div>
    </section>
);

export default Secretariat;
